import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  getColor,
  InputField,
  Modal,
  Typography,
  useToast,
} from "fds";
import { v4 as uuidv4 } from "uuid";
import "./index.scss";
import useLoginStore from "../../redux/dispatcher/useLoginStore";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useAdminLoginStore from "../../redux/dispatcher/useAdminLoginStore";
import {
  getRedirectUrl,
  getUserInfoFromLocalStorage,
  removeRedirectUrl,
} from "../../shared/utils/auth";
import { LoginDto } from "../../types/auth";

interface MailOTPLoginModalProps {
  open: boolean;
  requestId: string;
  isAdmin?: boolean;
  changeLoading: (status: boolean) => void;
  onClose: () => void;
  isMobile?: boolean;
}
const MailOTPLoginModal: React.FC<MailOTPLoginModalProps> = ({
  open,
  requestId,
  changeLoading,
  isAdmin,
  isMobile,
  onClose,
}) => {
  const user = getUserInfoFromLocalStorage() as string | "";
  const loginDto: LoginDto = JSON.parse(user);

  const toast = useToast();
  const interval = useRef<any>();
  const { sendOtpEmail, checkOtpEmail } = useLoginStore();
  const { adminOTPLogin, adminSendOTPEMail } = useAdminLoginStore();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [counter, changeCounter] = useState(-1); // -1 일 경우 비활성, 0 보다 글경우 활성화
  const [otp, changeOtp] = useState("");
  const [hasError, setHasError] = useState(false);
  const [trialCount, changeTrialCount] = useState(0);
  useEffect(() => {
    if (counter === 0) {
      toast.toastMsg(uuidv4(), t("login.mailOTP.OTPOver"), "error");
      clearInterval(interval.current);
    }
  }, [counter]);

  const timer = () => {
    changeCounter(180);
    setHasError(false);
    if (counter <= 1) {
      changeCounter(180);
    }
    interval.current = setInterval(() => {
      changeCounter((counter) => counter - 1);
    }, 1000);
    return () => clearInterval(interval.current);
  };
  /** OTP 메일 전송을 위한 요청 */
  const doOtpReqeust = async (value: string): Promise<void> => {
    changeLoading(true);
    const resp: any = await (window.location.href.includes("/admin")
      ? adminSendOTPEMail(value)
      : sendOtpEmail({ userId: value }));
    if (resp.type.includes("fulfilled")) {
      changeLoading(false);
      toast.toastMsg(uuidv4(), t("login.findPW.sendMail"), "success");
      changeTrialCount(trialCount + 1);
      timer();
    } else {
      changeLoading(false);
      toast.toastMsg(uuidv4(), t("login.mailOTP.mailSendFail"), "error");
    }
  };
  /**OTP를 통해서 실질적으로 로그인 */
  const confirmOtpLogin = async () => {
    changeLoading(true);
    let resp: any = await (window.location.href.includes("/admin")
      ? adminOTPLogin(otp.trim())
      : checkOtpEmail({ mailOTP: otp.trim() }));
    if (resp.payload) {
      changeLoading(false);
      let redirectUrl = getRedirectUrl();
      if (redirectUrl && !redirectUrl.includes("/login")) {
        removeRedirectUrl();
        try {
          const url = new URL(redirectUrl);
          navigate(url.pathname);
        } catch (err) {
          window.location.replace(redirectUrl);
        }
      } else {
        const defaultTenancy = resp.payload?.defaultTenancy ?? "My Space";
        const featureAccessType =
          resp.payload?.ecoSystemLicenseStats?.featureAccessType ?? "";
        navigate(
          isAdmin
            ? `${process.env.PUBLIC_URL}/admin/home`
            : featureAccessType === "LITE"
            ? `${process.env.PUBLIC_URL}/myInbox/received`
            : window.eco.serviceType === "OCI" && defaultTenancy
            ? `${process.env.PUBLIC_URL}/${defaultTenancy}/home`
            : `${process.env.PUBLIC_URL}/home`
        );
      }
    } else {
      changeLoading(false);
      setHasError(true);
    }
  };
  return (
    <Modal
      open={open}
      isMobile={isMobile}
      isExit={!isMobile}
      onClose={onClose}
      title={t("login.mailOTP.title")}
      subTitle={
        <Typography
          type="body4_rg"
          exactColor={getColor("COLOR_TEXT_SECONDARY")}
          whiteSpace="break-spaces"
        >
          {t("login.mailOTP.titleDesc")}
        </Typography>
      }
      width={540}
      body={
        <Box direction="column" height={"100%"} minHeight={73}>
          <Box classes="mg mt-16">
            <InputField
              placeholder=" "
              size="md"
              value={requestId}
              disabled
              width={"100%"}
            />
            {!isMobile && (
              <Button
                btnStyle="fill"
                label={
                  trialCount === 0
                    ? t("login.mailOTP.request")
                    : t("login.mailOTP.resendBtn")
                }
                classes={isMobile ? "mg mt-40" : "mg ml-8"}
                whiteSpace="pre"
                state={counter <= 0 ? "default" : "disable"}
                onClick={() => doOtpReqeust(requestId)}
              />
            )}
          </Box>
          {counter !== -1 && (
            <InputField
              size="md"
              width={"100%"}
              classes="mg mt-8"
              placeholder={t("login.mailOTP.requestPlaceholder")}
              isError={hasError}
              helpMessage={
                hasError ? t("login.mailOTP.codeNotMatch") : undefined
              }
              value={otp}
              onChange={(e) => changeOtp(e.target.value)}
              maxLength={32}
              disabled={counter === 0}
              type="positive-integer"
              trailingIcon={
                <Typography
                  type="body2_rg"
                  exactColor={
                    counter === 0
                      ? getColor("COLOR_TEXT_INFO")
                      : getColor("COLOR_TEXT_INTERACTIVE_ERROR")
                  }
                >
                  {("" + Math.floor(counter / 60)).padStart(2, "0")}:
                  {("" + Math.floor(counter % 60)).padStart(2, "0")}
                </Typography>
              }
            />
          )}
          {isMobile && (
            <Button
              btnStyle="fill"
              label={
                trialCount === 0
                  ? t("login.mailOTP.request")
                  : t("login.mailOTP.resendBtn")
              }
              classes={isMobile ? "mg mt-40" : "mg ml-8"}
              whiteSpace="pre"
              state={counter <= 0 ? "default" : "disable"}
              onClick={() => doOtpReqeust(requestId)}
            />
          )}
        </Box>
      }
      footer={
        isMobile ? (
          <Box width="100%">
            <Box onClick={onClose}>
              <Typography textAlign="center" type="body1_md">
                {t("common.cancel")}
              </Typography>
            </Box>
            <Box
              onClick={otp && counter >= 0 ? confirmOtpLogin : undefined}
              style={{
                cursor: otp && counter >= 0 ? "pointer" : "not-allowed",
              }}
            >
              <Typography
                textAlign="center"
                type="body1_md"
                exactColor={getColor(
                  otp && counter >= 0
                    ? "COLOR_TEXT_BRAND"
                    : "COLOR_TEXT_INTERACTIVE_BRAND_DISABLED"
                )}
              >
                {t("common.confirm")}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box>
            <Button
              btnStyle="fill"
              label={t("login.title")}
              state={otp && counter >= 0 ? "default" : "disable"}
              onClick={confirmOtpLogin}
            />
          </Box>
        )
      }
    />
  );
};
export default MailOTPLoginModal;
